import { useEffect, useState } from "react";
import { Plus, DollarSign, Trash2, Edit2, TrendingUp, TrendingDown, X, Check } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SOURCE_LABELS = {
  consulting: "Consulting",
  app_builds: "App Builds",
  ai_training: "AI Training",
  audit: "Audit",
  speaking: "Speaking",
  affiliate: "Affiliate",
  funding_readiness: "Funding Readiness",
  other: "Other",
};

const SOURCE_COLORS = {
  consulting: "#D4AF37",
  app_builds: "#0A0A0A",
  ai_training: "#8B7E5C",
  audit: "#5C4A2F",
  speaking: "#A89472",
  affiliate: "#C5A059",
  funding_readiness: "#5C5C5C",
  other: "#9E9E9E",
};

const fmt = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);

const fmtShort = (cents) => {
  const v = (cents || 0) / 100;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

export default function IncomePanel() {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [s, e] = await Promise.all([
      api.get("/admin/income/stats"),
      api.get("/admin/income"),
    ]);
    setStats(s.data);
    setEntries(e.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this entry?")) return;
    await api.delete(`/admin/income/${id}`);
    toast.success("Deleted");
    await load();
  };

  if (!stats) return <div className="p-12 text-center text-[var(--tmg-muted)]">Loading income data…</div>;

  const monthDelta = stats.last_month_cents
    ? Math.round(((stats.this_month_cents - stats.last_month_cents) / stats.last_month_cents) * 100)
    : null;
  const yearDelta = stats.last_year_cents
    ? Math.round(((stats.ytd_cents - stats.last_year_cents) / stats.last_year_cents) * 100)
    : null;

  return (
    <div className="p-8 sm:p-10 space-y-10" data-testid="income-panel">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="tmg-overline mb-2">— Income Dashboard</p>
          <h2 className="font-display text-2xl font-semibold">Track every dollar.</h2>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={stats.stripe_connected ? "border-green-600 text-green-700" : "border-[var(--tmg-line)] text-[var(--tmg-muted)]"}>
            Stripe: {stats.stripe_connected ? "Connected" : "Not configured"}
          </Badge>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-income-btn"
            className="tmg-btn-dark text-xs px-5 py-3"
          >
            <Plus className="w-4 h-4" /> Add Income
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-this-month" label="This Month" value={fmt(stats.this_month_cents)} delta={monthDelta} compareLabel="vs. last month" />
        <KpiCard testId="kpi-last-month" label="Last Month" value={fmt(stats.last_month_cents)} />
        <KpiCard testId="kpi-ytd" label="Year to Date" value={fmt(stats.ytd_cents)} delta={yearDelta} compareLabel="vs. last year" />
        <KpiCard testId="kpi-last-year" label="Last Year" value={fmt(stats.last_year_cents)} />
      </div>

      {/* Monthly chart */}
      <div className="border border-[var(--tmg-line)] bg-white p-6">
        <p className="tmg-overline mb-4">— Revenue Over Time</p>
        {stats.by_month.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-[var(--tmg-muted)] text-sm">
            Add your first income entry to see the chart populate.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.by_month} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D5" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5C5C5C" }} />
              <YAxis tick={{ fontSize: 10, fill: "#5C5C5C" }} tickFormatter={fmtShort} />
              <Tooltip
                formatter={(v) => fmt(v)}
                contentStyle={{ background: "#0A0A0A", border: "1px solid #D4AF37", color: "#fff" }}
                labelStyle={{ color: "#D4AF37", fontSize: 12 }}
              />
              <Bar dataKey="total_cents" fill="#D4AF37" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* By source */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-[var(--tmg-line)] bg-white p-6">
          <p className="tmg-overline mb-4">— Revenue by Source (YTD)</p>
          {stats.by_source.length === 0 ? (
            <p className="text-sm text-[var(--tmg-muted)]">No entries yet this year.</p>
          ) : (
            <div className="space-y-4">
              {stats.by_source.map((s) => {
                const top = stats.by_source[0].total_cents || 1;
                const pct = Math.round((s.total_cents / top) * 100);
                return (
                  <div key={s.source} data-testid={`source-row-${s.source}`}>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="font-display text-base font-medium">{SOURCE_LABELS[s.source] || s.source}</span>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-[var(--tmg-muted)] font-mono-sm">{s.count} entries</span>
                        <span className="font-semibold w-24 text-right">{fmt(s.total_cents)}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-[var(--tmg-line)] overflow-hidden">
                      <div className="h-full transition-all duration-1000" style={{ width: `${pct}%`, background: SOURCE_COLORS[s.source] || "#D4AF37" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 border border-[var(--tmg-line)] bg-[var(--tmg-obsidian)] text-white p-6">
          <p className="tmg-overline-gold mb-4">— Stripe Sync</p>
          {stats.stripe_connected ? (
            <p className="text-sm">
              Connected. Payments will appear automatically once we wire your account.
            </p>
          ) : (
            <>
              <p className="text-sm text-white/80 leading-relaxed mb-4">
                Stripe is not yet configured. When you&apos;re ready, add your Stripe API key to the
                backend environment and we&apos;ll auto-sync every Stripe payment into this dashboard —
                no double entry, no spreadsheets.
              </p>
              <code className="block bg-white/10 p-3 text-xs font-mono-sm">STRIPE_API_KEY=sk_live_...</code>
            </>
          )}
        </div>
      </div>

      {/* Entries table */}
      <div className="border border-[var(--tmg-line)] bg-white">
        <div className="p-6 border-b border-[var(--tmg-line)] flex items-center justify-between">
          <p className="tmg-overline">— Recent Entries ({entries.length})</p>
        </div>
        {entries.length === 0 ? (
          <div className="p-12 text-center text-[var(--tmg-muted)]">No income entries yet. Click &ldquo;Add Income&rdquo; to log your first one.</div>
        ) : (
          <div className="divide-y divide-[var(--tmg-line)]">
            {entries.map((it) => (
              <div key={it.id} data-testid={`income-row-${it.id}`} className="flex items-center gap-4 p-5 hover:bg-[var(--tmg-ivory)]">
                <div className="w-2 h-12" style={{ background: SOURCE_COLORS[it.source] || "#D4AF37" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <p className="font-semibold">{fmt(it.amount_cents)}</p>
                    <Badge variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)]">
                      {SOURCE_LABELS[it.source] || it.source}
                    </Badge>
                    {it.payment_method && it.payment_method !== "manual" && (
                      <Badge variant="outline" className="border-[var(--tmg-line)] text-[var(--tmg-muted)]">
                        {it.payment_method}
                      </Badge>
                    )}
                    {it.is_recurring && (
                      <Badge variant="outline" className="border-green-500 text-green-700">recurring</Badge>
                    )}
                  </div>
                  <p className="text-sm text-[var(--tmg-muted)] truncate">
                    {it.date} · {it.client_name || "—"}
                    {it.notes ? ` · ${it.notes}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => { setEditing(it); setShowForm(true); }} className="p-2 hover:bg-[var(--tmg-ivory-2)]" data-testid={`edit-${it.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(it.id)} className="p-2 hover:bg-red-50 hover:text-red-600" data-testid={`delete-income-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <IncomeForm
          editing={editing}
          sources={stats.valid_sources}
          methods={stats.valid_methods}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, delta, compareLabel, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[var(--tmg-line)] p-6">
      <p className="tmg-overline mb-3">{label}</p>
      <div className="font-display text-3xl font-semibold mb-2">{value}</div>
      {delta !== undefined && delta !== null && (
        <div className={`flex items-center gap-1 text-xs font-mono-sm ${delta >= 0 ? "text-green-700" : "text-red-600"}`}>
          {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {delta >= 0 ? "+" : ""}{delta}% {compareLabel}
        </div>
      )}
    </div>
  );
}

function IncomeForm({ editing, sources, methods, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: editing?.date || today,
    amount: editing ? (editing.amount_cents / 100).toFixed(2) : "",
    source: editing?.source || "consulting",
    client_name: editing?.client_name || "",
    notes: editing?.notes || "",
    payment_method: editing?.payment_method || "manual",
    tool_id: editing?.tool_id || "",
    is_recurring: editing?.is_recurring || false,
  });
  const [saving, setSaving] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amount_cents = Math.round(parseFloat(form.amount) * 100);
      if (!Number.isFinite(amount_cents) || amount_cents <= 0) {
        toast.error("Please enter a valid amount");
        setSaving(false);
        return;
      }
      const payload = {
        date: form.date,
        amount_cents,
        source: form.source,
        client_name: form.client_name,
        notes: form.notes,
        payment_method: form.payment_method,
        tool_id: form.tool_id,
        is_recurring: !!form.is_recurring,
      };
      if (editing) {
        await api.patch(`/admin/income/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/income", payload);
        toast.success("Income logged");
      }
      onSaved();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        data-testid="income-form"
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[var(--tmg-obsidian)] text-white p-6 flex items-center justify-between">
          <div>
            <p className="tmg-overline-gold mb-1">— {editing ? "Edit" : "Log New"} Income</p>
            <h3 className="font-display text-2xl font-semibold">
              {editing ? "Update entry" : "Add an entry"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="tmg-overline">Date *</Label>
              <Input type="date" required value={form.date} onChange={handle("date")} data-testid="income-date" />
            </div>
            <div className="space-y-2">
              <Label className="tmg-overline">Amount (USD) *</Label>
              <Input type="number" step="0.01" min="0" required placeholder="297.00" value={form.amount} onChange={handle("amount")} data-testid="income-amount" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="tmg-overline">Source *</Label>
              <Select value={form.source} onValueChange={handle("source")}>
                <SelectTrigger data-testid="income-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABELS[s] || s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="tmg-overline">Payment Method</Label>
              <Select value={form.payment_method} onValueChange={handle("payment_method")}>
                <SelectTrigger data-testid="income-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="tmg-overline">Client / payer name</Label>
            <Input value={form.client_name} onChange={handle("client_name")} placeholder="Acme Co." data-testid="income-client" />
          </div>

          {form.source === "affiliate" && (
            <div className="space-y-2">
              <Label className="tmg-overline">Affiliate tool</Label>
              <Input value={form.tool_id} onChange={handle("tool_id")} placeholder="nav, lendio, etc." />
            </div>
          )}

          <div className="space-y-2">
            <Label className="tmg-overline">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={handle("notes")} placeholder="Optional context, invoice #, etc." />
          </div>

          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_recurring}
              onChange={(e) => setForm((f) => ({ ...f, is_recurring: e.target.checked }))}
              className="w-4 h-4"
            />
            <span>This is a recurring payment</span>
          </label>
        </div>

        <div className="border-t border-[var(--tmg-line)] p-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="income-save-btn" className="tmg-btn-primary disabled:opacity-60">
            {saving ? "Saving…" : (<><Check className="w-4 h-4" /> Save Entry</>)}
          </button>
        </div>
      </form>
    </div>
  );
}
